import { useEffect, useRef, useState } from "react";
import { Mic, MicOff, Send, Volume2, VolumeX } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

import { supabase } from "../../../../lib/supabase";
import {
  fetchLatestConversation,
  Message,
  updateConversationState,
} from "../../data/concierge";

interface ConciergeState {
  // TODO: Define the type of the resources
  messages: Message[];
  resources: any[];
}

export interface ConciergePayload {
  schema: string;
  table: string;
  commit_timestamp: string;
  eventType: "INSERT" | "UPDATE" | "DELETE";
  new: {
    content: ConciergeState;
    uuid: string;
    mentee_uuid: string;
    created_at: Date;
  };
  old: Record<string, any>;
  errors: null | any;
}

export function ChatAgent() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);

  const [isListening] = useState(false);
  const [isVoiceMode] = useState(false);
  const [speechSupported] = useState(false);
  const [microphonePermission] = useState<
    "granted" | "denied" | "prompt" | "unknown"
  >("unknown");
  const [speechError, setSpeechError] = useState<string | null>(null);
  const synthRef = useRef<SpeechSynthesis>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // const [voiceInput, setVoiceInput] = useState<string>("");

  const recognitionRef = useRef<any>(null);

  // Fetch latest conversation on component mount
  useEffect(() => {
    loadLatestConversation();
  }, []);

  const loadLatestConversation = async () => {
    try {
      const conversation = await fetchLatestConversation();
      if (conversation) {
        setConversationId(conversation.uuid);
        setMessages(conversation.content.messages || []);
      } else {
        // If no conversation exists, create a new conversation ID
        setConversationId(crypto.randomUUID());
      }
    } catch (error) {
      console.error("Failed to fetch conversation:", error);
    }
  };

  const handleClearConversation = async () => {
    setMessages([]);
    setConversationId(crypto.randomUUID());
  };

  const handlePayload = (payload: ConciergePayload) => {
    console.log("PAYLOAD", payload);

    const type = payload.eventType;
    if (type == "DELETE") {
      return;
    }

    const payloadMessages = payload.new.content.messages;
    const lastMessage = payloadMessages[payloadMessages.length - 1];
    const resources = payload.new.content.resources || [];

    if (lastMessage.sender == "user") {
      console.log("Last message is a user message, skipping...");
      return;
    }

    setIsLoading(false);

    const newMessage: Message = {
      uuid: payload.new.uuid,
      content: lastMessage.content,
      sender: "assistant",
      timestamp: new Date(),
      resources: resources,
    };

    console.log("New messages: ", [...messages, newMessage]);

    // TODO: Need to display resources
    setMessages((prev) => [...prev, newMessage]);
  };

  useEffect(() => {
    const changes = supabase
      .channel("schema-db-changes")
      .on(
        // @ts-ignore
        "postgres_changes",
        {
          event: "*", // Listen only to INSERTs
          schema: "public",
          table: "concierge_messages",
        },
        (payload: ConciergePayload) => handlePayload(payload)
      )
      .subscribe();

    return () => {
      changes.unsubscribe();
    };
  }, []);

  const handleSubmit = () => {
    if (!input.trim()) return;

    const userMessage: Message = {
      uuid: crypto.randomUUID(),
      content: input,
      sender: "user",
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);

    // Pass the conversation ID to updateConversationState
    updateConversationState(userMessage, conversationId!);
  };

  const startVoiceInput = async () => {
    if (!recognitionRef.current || !speechSupported) {
      return;
    }

    if (microphonePermission === "denied") {
      return;
    }

    try {
      recognitionRef.current.start();
    } catch (error) {
      console.error("Error starting speech recognition:", error);
    }
  };

  const stopVoiceInput = () => {
    if (recognitionRef.current && isListening) {
      recognitionRef.current.stop();
    }
  };

  const startListening = async () => {
    if (!recognitionRef.current) {
      setSpeechError("Speech recognition is not available.");
      return;
    }

    if (isListening) return;

    setSpeechError(null);

    // If permission is denied, show error
    if (microphonePermission === "denied") {
      setSpeechError(
        "Microphone access is denied. Please enable microphone access in your browser settings and refresh the page."
      );
      return;
    }

    try {
      recognitionRef.current.start();
    } catch (error) {
      console.error("Error starting speech recognition:", error);
      setSpeechError("Failed to start speech recognition. Please try again.");
    }
  };

  const stopListening = () => {
    if (recognitionRef.current && isListening) {
      recognitionRef.current.stop();
    }
    setSpeechError(null);
  };

  // const handleVoiceStateChange = (
  //   chatIsListening: boolean,
  //   chatIsVoiceMode: boolean
  // ) => {
  //   setIsVoiceMode(chatIsVoiceMode);
  //   // If chat is listening, don't allow prominent section to listen
  // };

  // const handleVoiceInputProcessed = () => {
  //   setVoiceInput("");
  // };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="mx-auto w-full max-w-4xl">
      {/* <div className="mb-8">
        <Card className="border-primary/20 bg-gradient-to-r from-primary/5 to-primary/10">
          <CardContent className="p-8">
            <div className="flex flex-col items-center space-y-4 text-center">
              <div className="mb-2">
                <h3 className="text-2xl font-bold text-primary">
                  Tell Me What's Happening at Work
                </h3>
              </div>
              <p className="max-w-2xl text-muted-foreground">
                Simply speak about what's on your mind. Our AI mentor is ready
                to listen and provide personalized guidance specific to your
                role. Your spoken words will appear in the chat box below.
              </p>

              <div className="mt-6 flex flex-col items-center space-y-4">
                {speechSupported && microphonePermission !== "denied" ? (
                  <Button
                    size="lg"
                    onClick={isListening ? stopVoiceInput : startVoiceInput}
                    disabled={isVoiceMode} // Disable if chat voice mode is active
                    className={`relative size-24 overflow-hidden rounded-full border-0 p-0 transition-all duration-300 ${
                      isListening
                        ? "animate-pulse shadow-lg shadow-primary/30 hover:scale-105"
                        : isVoiceMode
                          ? "cursor-not-allowed bg-muted hover:bg-muted"
                          : "shadow-lg hover:scale-105 hover:shadow-xl hover:shadow-primary/30"
                    }`}
                  >
                    {isListening ? (
                      <div className="relative">
                        <AIIconWave size={42} />
                        <div className="absolute inset-0 animate-ping rounded-full bg-white/20" />
                      </div>
                    ) : (
                      <AIIcon size={42} />
                    )}
                  </Button>
                ) : (
                  <div className="flex size-24 items-center justify-center rounded-full bg-muted">
                    <AIIcon size={42} />
                  </div>
                )}

                {isListening ? (
                  <div className="flex items-center gap-2">
                    <div
                      className="size-2 animate-pulse rounded-full"
                      style={{ backgroundColor: "#5ac0fa" }}
                    />
                    <Badge
                      variant="secondary"
                      className="animate-pulse border-primary/30"
                      style={{
                        backgroundColor: "#5ac0fa20",
                        color: "#5ac0fa",
                        borderColor: "#5ac0fa50",
                      }}
                    >
                      AI is listening... Click to stop
                    </Badge>
                  </div>
                ) : isVoiceMode ? (
                  <Badge
                    variant="secondary"
                    className="border-primary/20"
                    style={{
                      backgroundColor: "#5ac0fa20",
                      color: "#5ac0fa",
                      borderColor: "#5ac0fa40",
                    }}
                  >
                    Voice mode active in chat below
                  </Badge>
                ) : speechSupported && microphonePermission !== "denied" ? (
                  <Badge
                    variant="secondary"
                    className="border-primary/20"
                    style={{
                      backgroundColor: "#5ac0fa20",
                      color: "#5ac0fa",
                      borderColor: "#5ac0fa40",
                    }}
                  >
                    Click the AI to start speaking
                  </Badge>
                ) : (
                  <Badge
                    variant="secondary"
                    className="border-primary/20"
                    style={{
                      backgroundColor: "#5ac0fa20",
                      color: "#5ac0fa",
                      borderColor: "#5ac0fa40",
                    }}
                  >
                    Click the AI to start speaking
                  </Badge>
                )}

                
                {speechSupported && (
                  <div className="mt-4 text-center">
                    <p className="text-xs text-muted-foreground">
                      💡 Tip: Speak naturally and your words will appear in the
                      chat input box below where you can edit before sending.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div> */}

      <div className="mb-6">
        <h2 className="flex items-center justify-between text-xl font-semibold">
          Share Your Challenges
          <button
            onClick={handleClearConversation}
            className="rounded-lg border bg-gray-100 px-4 py-2 text-sm text-gray-600 font-normal transition-colors hover:bg-red-200 hover:text-gray-900"
          >
            Clear Conversation
          </button>
        </h2>
        <p className="text-muted-foreground">
          Discuss your sales challenges, wins, and questions in a supportive
          environment.
        </p>
      </div>

      <div className="rounded-xl border bg-white shadow-sm">
        <div
          className={`flex flex-col ${messages.length === 0 ? "min-h-[200px] max-h-[400px]" : "h-[600px]"}`}
        >
          <div
            className={`flex-1 space-y-4 overflow-y-auto p-6 ${messages.length === 0 ? "flex items-center justify-center" : ""}`}
          >
            {messages.length === 0 ? (
              <div className="text-center text-muted-foreground">
                <p className="text-lg font-medium">Start a conversation</p>
                <p className="text-sm">
                  Share your challenges and get personalized guidance
                </p>
              </div>
            ) : (
              <>
                {messages.map((message) => (
                  <div
                    key={message.uuid}
                    className={`flex ${message.sender === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-lg p-4 ${
                        message.sender === "user"
                          ? "bg-primary text-white"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      <div>{message.content}</div>
                      {/* Display resources if present and this is an assistant message */}
                      {message.sender === "assistant" &&
                        message.resources &&
                        message.resources.length > 0 && (
                          <div className="mt-2 border-t pt-2 text-xs text-gray-500">
                            <div className="mb-1 font-semibold">
                              References:
                            </div>
                            <ul className="space-y-1">
                              {message.resources.map((res, i) => (
                                <li key={i} className="flex flex-col">
                                  <span>{res.content}</span>
                                  {res.metadata?.file_name && (
                                    <span className="italic">
                                      ({res.metadata.file_name})
                                    </span>
                                  )}
                                  {res.metadata?.source && (
                                    <a
                                      href={res.metadata.source}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-blue-500 underline"
                                    >
                                      View Resource
                                    </a>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                    </div>
                  </div>
                ))}

                {isLoading && (
                  <div className="flex justify-start">
                    <div className="max-w-[80%] rounded-lg bg-gray-100 p-4">
                      <div className="flex items-center space-x-2">
                        <div
                          className="size-2 animate-bounce rounded-full bg-gray-400"
                          style={{ animationDelay: "0ms" }}
                        />
                        <div
                          className="size-2 animate-bounce rounded-full bg-gray-400"
                          style={{ animationDelay: "150ms" }}
                        />
                        <div
                          className="size-2 animate-bounce rounded-full bg-gray-400"
                          style={{ animationDelay: "300ms" }}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <form onSubmit={handleSubmit} className="border-t p-4">
            <div className="flex gap-2">
              <Textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={
                  isVoiceMode
                    ? "Type or speak your message... Press Enter to send, Shift+Enter for new line"
                    : "Share what's on your mind... Press Enter to send, Shift+Enter for new line"
                }
                className="flex-1 min-h-[60px] resize-none bg-input-background"
                disabled={isLoading}
              />

              <div className="flex flex-col gap-2">
                <div className="flex flex-col gap-1">
                  <Button
                    size="sm"
                    variant={isVoiceMode ? "default" : "outline"}
                    onClick={() => {}}
                    className={`flex items-center gap-1 ${isVoiceMode ? "bg-primary hover:bg-primary/90" : ""}`}
                    disabled={!speechSupported && !synthRef.current}
                  >
                    {isVoiceMode ? (
                      <Volume2 className="size-4" />
                    ) : (
                      <VolumeX className="size-4" />
                    )}
                  </Button>

                  {isVoiceMode && speechSupported && !speechError && (
                    <Button
                      size="sm"
                      variant={isListening ? "destructive" : "secondary"}
                      onClick={isListening ? stopListening : startListening}
                      disabled={isLoading || microphonePermission === "denied"}
                      className={`flex items-center gap-1 ${isListening ? "animate-pulse" : ""}`}
                    >
                      {isListening ? (
                        <MicOff className="size-4" />
                      ) : (
                        <Mic className="size-4" />
                      )}
                    </Button>
                  )}
                </div>

                {/* Send Button */}
                <Button
                  type="submit"
                  disabled={!input || !input.trim() || isLoading}
                  className="self-end bg-primary hover:bg-primary/90"
                >
                  <Send className="size-4" />
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
